import styled, { css } from "styled-components"
import { isUndefined } from "lodash"

import colors from "../utils/colors"

export interface ColumnProps {
  leftSpanDesk?: number
  rightSpanDesk?: number
  leftSpanTab?: number
  rightSpanTab?: number
  center?: boolean
  alignLeft?: boolean
  marginBottom?: number
  marginBottomTab?: number
}

interface GridColumnContainerProps {
  columns: number
  leftSpanDesk?: number
  rightSpanDesk?: number
  leftSpanTab?: number
  rightSpanTab?: number
}

export const breakpoints = {
  mobileMax: "767px",
  tabletMin: "768px",
  tabletMax: "991px",
  desktopMin: "992px",
}

export const nav = {
  desktop: "96px",
  mobile: "80px",
}

export const footer = {
  desktop: "80px",
  mobile: "240px",
}

export const padding = {
  desktop: "48px",
  mobile: "24px",
}

export const Spacer = styled.div<{
  heightMobile?: number
  heightTablet?: number
  heightDesk?: number
}>`
  ${props => `height: calc(5em * ${isUndefined(props.heightMobile) ? 1 : props.heightMobile});`};
  width: 100%;

  @media (min-width: ${breakpoints.tabletMin}) {
    ${props => `height: calc(5em * ${isUndefined(props.heightTablet) ? 1 : props.heightTablet});`};
  }

  @media (min-width: ${breakpoints.desktopMin}) {
    ${props => `height: calc(5em * ${isUndefined(props.heightDesk) ? 1 : props.heightDesk});`};
  }
`

const roundedContainerStyles = css`
  border-radius: 12px;

  padding: 0 ${padding.mobile} ${padding.mobile} ${padding.mobile};

  @media (min-width: ${breakpoints.tabletMin}) {
    padding: 0 ${padding.desktop} ${padding.desktop} ${padding.desktop};
  }
`

export const RootLayout = styled.div<{ ignorePadding?: boolean }>`
  ${props => (!props.ignorePadding ? `padding: 0 ${padding.mobile};` : "")}

  @media (min-width: ${breakpoints.tabletMin}) {
    ${props => (!props.ignorePadding ? `padding: 0 ${padding.desktop};` : "")}
  }
`

export const GridRoot = styled(RootLayout)<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  position: relative;
  ${props => (props.fullWidth ? "width: 100%;" : "")}
`

export const ColumnContainer = styled(GridRoot)<{
  breakAtMobile?: boolean
  breakAtTab?: boolean
}>`
  > * {
    box-sizing: border-box;
    flex: 0 0 calc(50% - 14px);

    ${props =>
      props.breakAtMobile === false
        ? ""
        : `
          @media (max-width: ${breakpoints.mobileMax}) {
            flex: 0 0 100%;
          }
        `}

    ${props =>
      props.breakAtTab === true
        ? `
          @media (max-width: ${breakpoints.tabletMax}) {
            flex: 0 0 100%;
          }
        `
        : ""}
  }
`

export const SingleColumnContainer = styled(GridRoot)<{ backgroundColor?: string }>`
  ${props => (props.backgroundColor ? `background-color: ${props.backgroundColor};` : "")}

  > div {
    display: flex;
    flex-direction: column;
  }
`

export const DualColumnContainer = styled(GridRoot)<{
  breakAtMobile?: boolean
  breakAtTab?: boolean
}>`
  > * {
    box-sizing: border-box;
    flex: 0 0 calc(50% - 14px);

    ${props =>
      props.breakAtMobile === false
        ? ""
        : `
          @media (max-width: ${breakpoints.mobileMax}) {
            flex: 0 0 100%;
          }
        `}

    ${props =>
      props.breakAtTab === true
        ? `
          @media (max-width: ${breakpoints.tabletMax}) {
            flex: 0 0 100%;
          }
        `
        : ""}
  }
`

export const TripleColumnContainer = styled(GridRoot)`
  > * {
    box-sizing: border-box;
    flex: 0 0 100%;

    @media (min-width: ${breakpoints.tabletMin}) {
      flex: 0 0 calc(50% - 14px);
    }

    @media (min-width: ${breakpoints.desktopMin}) {
      flex: 0 0 calc(33.333% - 14px);
    }
  }
`

export const QuinColumnContainer = styled(GridRoot)`
  > * {
    box-sizing: border-box;
    flex: 0 0 50%;

    @media (min-width: ${breakpoints.tabletMin}) {
      flex: 0 0 calc(50% - 14px);
    }

    @media (min-width: ${breakpoints.desktopMin}) {
      flex: 0 0 calc(20% - 5px);
    }
  }
`

export const Column = styled.div<ColumnProps>`
  position: relative;
  width: 100%;

  @media (min-width: ${breakpoints.tabletMin}) {
    ${props =>
      props.leftSpanTab !== undefined ? `padding-left: calc(${props.leftSpanTab} * 8.333%);` : ""}
    ${props =>
      props.rightSpanTab !== undefined
        ? `padding-right: calc(${props.rightSpanTab} * 8.333%);`
        : ""}
    ${props => props.marginBottomTab !== undefined && `margin-bottom: ${props.marginBottomTab}rem;`}
      
  }

  @media (min-width: ${breakpoints.desktopMin}) {
    ${props =>
      props.leftSpanDesk !== undefined ? `padding-left: calc(${props.leftSpanDesk} * 8.333%);` : ""}
    ${props =>
      props.rightSpanDesk !== undefined
        ? `padding-right: calc(${props.rightSpanDesk} * 8.333%);`
        : ""}
  }

  ${props =>
    props.center
      ? `
          align-items: center;
        `
      : ""}

  ${props => (props.alignLeft ? `text-align: left;` : "")}

  ${props => props.marginBottom !== undefined && `margin-bottom: ${props.marginBottom}rem;`}

`

export const SizedColumn = styled(Column)<{ colSizeDesk?: number; colSizeTab?: number }>`
  @media (min-width: ${breakpoints.tabletMin}) {
    ${props =>
      props.colSizeTab !== undefined ? `width: calc(${props.colSizeTab} * 8.333% - 14px);` : ""}
  }

  @media (min-width: ${breakpoints.desktopMin}) {
    ${props =>
      props.colSizeDesk !== undefined ? `width: calc(${props.colSizeDesk} * 8.333% - 14px);` : ""}
  }
`

export const Section = styled.div<{ backgroundColor?: string }>`
  padding: 50px 0;

  ${props => (props.backgroundColor ? `background-color: ${props.backgroundColor};` : "")}
`

export const RoundedContainer = styled(Column)`
  color: ${colors.white};
  background-color: ${colors.dark};
  ${roundedContainerStyles}

  p.large {
    margin-top: 0;
  }
`

/**
 * Only use for very special cases.
 * A modified GridRoot to use with GridColumn for grid layout so
 * that sections within an immediate children (GridColumn) will have the same height
 * across it's other immediate children
 */
export const GridColumnContainer = styled(GridRoot)<GridColumnContainerProps>`
  @media (min-width: ${breakpoints.tabletMin}) {
    ${props =>
      props.leftSpanTab !== undefined ? `padding-left: calc(${props.leftSpanTab} * 8.333%);` : ""}
    ${props =>
      props.rightSpanTab !== undefined
        ? `padding-right: calc(${props.rightSpanTab} * 8.333%);`
        : ""}
  }

  @media (min-width: ${breakpoints.desktopMin}) {
    display: grid;
    grid-column-gap: calc(14px);
    ${props => `grid-template-columns: repeat(${props.columns}, 1fr);`}

    ${props =>
      props.leftSpanDesk !== undefined ? `padding-left: calc(${props.leftSpanDesk} * 8.333%);` : ""}
    ${props =>
      props.rightSpanDesk !== undefined
        ? `padding-right: calc(${props.rightSpanDesk} * 8.333%);`
        : ""}
  }
`

export const TripleGridColumnContainer = styled(GridColumnContainer)`
  grid-template-columns: 100%;

  @media (min-width: ${breakpoints.desktopMin}) {
    ${props =>
      `grid-template-columns: ${props.columns < 3 ? "0%" : ""} repeat(${
        props.columns
      }, calc(33.333% - 14px)) ${props.columns < 3 ? "0%" : ""};`}

    ${props =>
      props.columns < 3 &&
      `
      > *:first-child {
        grid-column-start: 2;
      }
    `}

    ${props =>
      props.columns < 3 &&
      Array.from(
        { length: props.columns },
        (_, index) => `
      > *:nth-child(${index + 1}) > * {
        grid-column: ${index + 2};
      }
    `
      )}
  }
`

/**
 * Most styles won't work with display: contents element
 */
export const GridColumn = styled(Column)<{ noOfChildren?: number }>`
  @media (min-width: ${breakpoints.desktopMin}) {
    display: contents;

    > * {
      padding-left: ${padding.desktop};
      padding-right: ${padding.desktop};
    }
  }

  ${props =>
    Array.from(
      { length: props.noOfChildren || 10 },
      (_, index) => `
    > *:nth-child(${index + 1}) {
      grid-row: ${index + 1};
    }
  `
    )}
`

export const RoundedGridColumn = styled.div`
  ${roundedContainerStyles}

  margin-bottom: 1.5em;

  @media (min-width: ${breakpoints.desktopMin}) {
    margin-bottom: 0;
  }
`
